// Modèle de lecture local: ce que le serveur a répondu, rangé par entité, pour
// répondre aux écrans quand le réseau manque — avec les mêmes formes de réponse.
import { ALIAS_DE_LECTURE, TYPES_COUVERTS_PAR_LE_PULL, typeDeLecture } from './kinds';
import {
  apprendreForme,
  derniereSynchroLocale,
  ecrireEntitesServeur,
  ecrireLecture,
  formeDe,
  idDe,
  lectureParChemin,
  lireEntite,
  lireEntites,
  lireLecture,
  lireLigne,
  marquerDetailRecu,
} from './store';
import {
  cleDeLecture,
  copie,
  estParametreDePage,
  estParametreDeRecherche,
  separerDernierSegment,
} from './paths';
import { ajusteurDEntetes, ajusteurDeListe, filtreDeListe, resolveurPour } from './derived/registre';

const estObjet = (valeur) => valeur !== null && typeof valeur === 'object' && !Array.isArray(valeur);

/** Une réponse qui est une liste d'objets: tableau nu ou enveloppe `data` / `items`. */
export function extraireListe(corps) {
  if (Array.isArray(corps)) {
    if (!corps.every(estObjet)) return null;
    return { items: corps, wrap: 'array', reste: null, meta: null };
  }
  if (!estObjet(corps)) return null;
  for (const cle of ['data', 'items']) {
    if (Array.isArray(corps[cle]) && corps[cle].every(estObjet)) {
      const reste = {};
      Object.keys(corps).forEach((nom) => {
        if (nom === cle || nom === 'meta' || nom === 'pagination') return;
        const valeur = corps[nom];
        if (
          valeur === null ||
          ['string', 'number', 'boolean'].includes(typeof valeur) ||
          (estObjet(valeur) && JSON.stringify(valeur).length < 20000)
        ) {
          reste[nom] = valeur;
        }
      });
      return {
        items: corps[cle],
        wrap: cle,
        reste,
        meta: estObjet(corps.meta) ? corps.meta : estObjet(corps.pagination) ? corps.pagination : null,
        metaNom: estObjet(corps.meta) ? 'meta' : estObjet(corps.pagination) ? 'pagination' : null,
      };
    }
  }
  return null;
}

/** Garde ce que la lecture a montré, avant de le servir sans réseau. */
export async function enregistrerLecture({ chemin, parametres, data, status = 200, headers = {}, demandeeLe = null }) {
  const cle = cleDeLecture(chemin, parametres);
  const liste = extraireListe(data);
  const totalEntete = headers['x-total-count'] ?? headers['X-Total-Count'] ?? null;
  const entetesGardes = Object.fromEntries(
    Object.entries(headers || {}).filter(([nom]) => /^x-(total|count)/i.test(nom)),
  );

  if (liste && liste.items.every((item) => idDe(item))) {
    const kind = typeDeLecture(chemin);
    await ecrireEntitesServeur(kind, liste.items, { reponseDemandeeLe: demandeeLe });
    await ecrireLecture({
      ts: demandeeLe ?? undefined,
      key: cle,
      path: chemin,
      kind,
      collection: true,
      params: parametres,
      ids: liste.items.map(idDe),
      meta: liste.meta,
      total: totalEntete,
      entetes: entetesGardes,
    });
    await apprendreForme(kind, {
      list: liste.wrap,
      reste: liste.reste || {},
      metaNom: liste.metaNom || null,
      metaModele: liste.meta || null,
      enteteTotal: totalEntete !== null,
    });
    return;
  }

  const separation = separerDernierSegment(chemin);
  if (separation && estObjet(data)) {
    let entite = null;
    let forme = 'raw';
    if (idDe(data) === separation.id) entite = data;
    else if (estObjet(data.data) && idDe(data.data) === separation.id) {
      entite = data.data;
      forme = 'data';
    }
    if (entite) {
      await ecrireEntitesServeur(separation.parent, [entite], { reponseDemandeeLe: demandeeLe });
      // Cette entité a été reçue en détail: elle peut être montrée telle quelle avant le serveur.
      await marquerDetailRecu(separation.parent, idDe(entite), demandeeLe ?? Date.now());
      await apprendreForme(separation.parent, { detail: forme });
      return;
    }
  }

  await ecrireLecture({
    ts: demandeeLe ?? undefined,
    key: cle,
    path: chemin,
    collection: false,
    params: parametres,
    data,
    status,
    headers: entetesGardes,
  });
}

// ---------------------------------------------------------------------------
// Filtres de liste
// ---------------------------------------------------------------------------

const texteDe = (valeur) => {
  if (valeur === null || valeur === undefined) return '';
  if (typeof valeur === 'object') return JSON.stringify(valeur);
  return String(valeur);
};

const SANS_FILTRE = new Set([
  'from', 'to', 'date_from', 'date_to', 'start_date', 'end_date', 'start', 'end', 'since',
  'updated_since', 'month', 'year', 'period', 'include_deleted', 'include', 'fields',
]);

export function correspondAuxParametres(item, parametres) {
  return Object.entries(parametres || {}).every(([nom, valeur]) => {
    if (estParametreDePage(nom) || SANS_FILTRE.has(nom)) return true;
    if (estParametreDeRecherche(nom)) {
      const aiguille = String(valeur).trim().toLowerCase();
      if (!aiguille) return true;
      return Object.values(item).some((champ) => texteDe(champ).toLowerCase().includes(aiguille));
    }
    if (!(nom in item)) return true;
    const attendues = String(valeur).split(',').map((v) => v.trim().toLowerCase());
    const reel = item[nom];
    if (Array.isArray(reel)) return reel.some((v) => attendues.includes(texteDe(v).toLowerCase()));
    return attendues.includes(texteDe(reel).toLowerCase());
  });
}

/**
 * Chaque filtre de la lecture se vérifie-t-il sur cette ligne ? Un paramètre qui
 * n'est pas un champ de la ligne (`status_group`, `parked`…) est une règle du
 * serveur que l'appareil ne connaît pas.
 */
const filtresCompris = (item, parametres) =>
  Object.keys(parametres || {}).every(
    (nom) => estParametreDePage(nom) || SANS_FILTRE.has(nom) || estParametreDeRecherche(nom) || nom in item,
  );

const instantDe = (item) => {
  const instant = Date.parse(item?.created_at || '');
  return Number.isFinite(instant) ? instant : null;
};

const nombre = (valeur) => {
  const n = Number.parseInt(valeur, 10);
  return Number.isFinite(n) ? n : null;
};

function pagination(parametres, total) {
  const taille = nombre(parametres.page_size ?? parametres.limit ?? parametres.per_page ?? parametres.size);
  if (!taille || taille <= 0) return { debut: 0, fin: total, page: 1, taille: total || 1 };
  const decalage = nombre(parametres.offset ?? parametres.skip);
  const page = nombre(parametres.page);
  const debut = decalage !== null ? decalage : ((page || 1) - 1) * taille;
  return { debut, fin: debut + taille, page: page || Math.floor(debut / taille) + 1, taille };
}

const parDateDecroissante = (a, b) => {
  const da = String(a.created_at || a.updated_at || '');
  const db = String(b.created_at || b.updated_at || '');
  if (da === db) return 0;
  return da < db ? 1 : -1;
};

function envelopper(forme, items, parametres, total, tailleDePage) {
  const wrap = forme?.list || 'array';
  if (wrap === 'array') return items;
  const page = nombre(parametres.page) || 1;
  const taille = tailleDePage || items.length || 1;
  const modele = forme?.metaModele;
  const resultat = { ...(forme?.reste || {}), [wrap]: items };
  ['total', 'count', 'total_items'].forEach((cle) => {
    if (typeof resultat[cle] === 'number') resultat[cle] = total;
  });
  if (typeof resultat.page === 'number') resultat.page = page;
  if (modele) {
    const meta = { ...modele };
    if ('page' in meta) meta.page = page;
    if ('page_size' in meta) meta.page_size = taille;
    if ('total_items' in meta) meta.total_items = total;
    if ('total' in meta) meta.total = total;
    if ('count' in meta) meta.count = items.length;
    if ('pages' in meta) meta.pages = Math.max(Math.ceil(total / taille), 1);
    if ('total_pages' in meta) meta.total_pages = Math.max(Math.ceil(total / taille), 1);
    if ('has_next' in meta) meta.has_next = page * taille < total;
    if ('has_previous' in meta) meta.has_previous = page > 1;
    if ('has_more' in meta) meta.has_more = page * taille < total;
    resultat[forme.metaNom || 'meta'] = meta;
  }
  return resultat;
}

async function reconstruireListe(chemin, parametres, entree) {
  const kind = typeDeLecture(chemin);
  const alias = ALIAS_DE_LECTURE[chemin];
  const forme = await formeDe(kind);
  let tous = (await lireEntites(kind)).filter((item) => (alias ? alias.filtre(item) : true));
  // Un type qui connaît les filtres du serveur (les trajets) les applique ici;
  // sinon, chaque paramètre est comparé au champ du même nom.
  const filtreDuType = filtreDeListe(kind);
  const correspondants = filtreDuType
    ? await filtreDuType(tous, parametres || {})
    : tous.filter((item) => correspondAuxParametres(item, parametres));
  const parId = new Map(correspondants.map((item) => [String(item.id), item]));

  let items;
  let total;
  let tailleDePage = null;
  if (entree?.ids) {
    // Une page déjà vue: son ordre est celui du serveur; les nouveautés (écrites
    // ici ou reçues par la synchronisation) passent en tête de la première page.
    // Une nouveauté est créée après la lecture de cette page par le serveur: les
    // autres lignes que la synchronisation a apportées sont sur d'autres pages,
    // quel que soit l'ordre de la liste (date, code, nom). Et sous un filtre que
    // l'appareil ne sait pas vérifier, rien n'est ajouté: la page du serveur
    // reste telle quelle jusqu'à sa revalidation.
    const connus = new Set(entree.ids.map(String));
    const dansLaPage = entree.ids.map((id) => parId.get(String(id))).filter(Boolean);
    const premierePage = !nombre(parametres.page) || nombre(parametres.page) === 1;
    const sansDecalage = !nombre(parametres.offset ?? parametres.skip);
    const instantsDeLaPage = dansLaPage.map(instantDe).filter((instant) => instant !== null);
    const seuil = Math.max(entree.ts || 0, ...instantsDeLaPage);
    const nouveautes =
      premierePage && sansDecalage
        ? correspondants
            .filter(
              (item) =>
                !connus.has(String(item.id)) &&
                (filtreDuType || filtresCompris(item, parametres)) &&
                (instantDe(item) ?? 0) > seuil,
            )
            .sort(parDateDecroissante)
        : [];
    tailleDePage = nombre(parametres.page_size ?? parametres.limit) || null;
    items = [...nouveautes, ...dansLaPage];
    // Jamais plus de lignes que la page demandée.
    if (tailleDePage) items = items.slice(0, tailleDePage);
    const base = nombre(entree.meta?.total_items ?? entree.meta?.total) ?? nombre(entree.total);
    const retirees = entree.ids.length - dansLaPage.length;
    total = Math.max((base ?? entree.ids.length) + nouveautes.length - retirees, items.length);
    tailleDePage = tailleDePage || entree.ids.length || null;
  } else {
    const ordonnes = [...correspondants].sort(parDateDecroissante);
    total = ordonnes.length;
    const fenetre = pagination(parametres, total);
    items = ordonnes.slice(fenetre.debut, fenetre.fin);
    tailleDePage = fenetre.taille;
  }
  const reponse = copie(envelopper(forme, items, parametres, total, tailleDePage));
  const ajusteur = ajusteurDeListe(kind);
  if (ajusteur && reponse && !Array.isArray(reponse)) ajusteur(reponse, items);
  let entetes = { ...(entree?.entetes || {}) };
  if (forme?.enteteTotal) entetes['x-total-count'] = String(total);
  const ajusteurEntetes = ajusteurDEntetes(kind);
  if (ajusteurEntetes) entetes = ajusteurEntetes(entetes, correspondants);
  return {
    data: reponse,
    headers: entetes,
    collection: true,
    vide: items.length === 0 && !entree && !forme,
  };
}

/**
 * Réponse locale à une lecture, ou null si rien n'est connu pour cette adresse.
 */
export async function lireHorsLigne({ chemin, parametres }) {
  const resolveur = resolveurPour(chemin);
  if (resolveur) {
    const calcule = await resolveur({ chemin, parametres });
    if (calcule !== undefined && calcule !== null) return { data: copie(calcule), headers: {} };
  }

  const cle = cleDeLecture(chemin, parametres);
  const entree = await lireLecture(cle);
  if (entree && entree.collection === false) {
    return { data: copie(entree.data), headers: entree.headers || {} };
  }

  const kind = typeDeLecture(chemin);
  const forme = await formeDe(kind);
  // `forme.list` n'existe que pour une adresse déjà vue comme une liste (ou un
  // alias de liste): une adresse de détail n'en a pas.
  if (entree?.collection || ALIAS_DE_LECTURE[chemin] || forme?.list) {
    return reconstruireListe(chemin, parametres, entree?.collection ? entree : null);
  }

  // Des lignes existent pour cette adresse (reçues par la synchronisation ou
  // saisies ici) sans qu'elle ait jamais été lue: une liste simple.
  if ((await lireEntites(kind)).length > 0) return reconstruireListe(chemin, parametres, null);

  const separation = separerDernierSegment(chemin);
  if (separation) {
    const entite = await lireEntite(separation.parent, separation.id);
    if (entite) {
      const formeParent = await formeDe(separation.parent);
      return {
        data: formeParent?.detail === 'data' ? { success: true, data: entite } : entite,
        headers: {},
      };
    }
  }

  // Même adresse lue avec d'autres paramètres: la dernière version consultée.
  const variantes = (await lectureParChemin(chemin)).filter((e) => e.collection === false);
  if (variantes.length > 0) {
    variantes.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    return { data: copie(variantes[0].data), headers: variantes[0].headers || {} };
  }
  return null;
}

/**
 * Lecture « local d'abord » d'une adresse déjà lue ici: la réponse locale et son
 * âge (`ts`: dernier instant où le serveur a confirmé ce contenu), ou null quand
 * l'adresse n'a jamais été lue (rien de fiable à montrer avant le serveur).
 *
 *  - Instantané d'un agrégat (chiffres, réglages): sa dernière réponse; avec des
 *    écritures encore à envoyer, le calcul local (qui les contient) passe avant.
 *  - Liste déjà vue: reconstruite depuis les entités locales (qui contiennent les
 *    écritures non envoyées et les mises à jour reçues depuis).
 *  - Détail d'une entité connue: l'entité locale.
 */
export async function lireLocalRapide({ chemin, parametres, enAttente = false }) {
  const cle = cleDeLecture(chemin, parametres);
  const entree = await lireLecture(cle);
  const kind = typeDeLecture(chemin);
  const synchro = TYPES_COUVERTS_PAR_LE_PULL.has(kind) ? await derniereSynchroLocale() : 0;

  if (entree && entree.collection === false) {
    if (enAttente) {
      const calcule = await lireHorsLigne({ chemin, parametres });
      if (calcule) return { data: calcule.data, headers: calcule.headers, ts: entree.ts || 0, origine: 'derive' };
    }
    return { data: copie(entree.data), headers: entree.headers || {}, ts: entree.ts || 0, origine: 'instantane' };
  }
  if (entree?.collection) {
    // Comme sans réseau: un calcul local propre à cette adresse passe avant la reconstruction.
    const locale = (await lireHorsLigne({ chemin, parametres })) || (await reconstruireListe(chemin, parametres, entree));
    return { data: locale.data, headers: locale.headers, ts: Math.max(entree.ts || 0, synchro), origine: 'liste' };
  }
  const separation = separerDernierSegment(chemin);
  if (separation && !ALIAS_DE_LECTURE[chemin]) {
    const ligne = await lireLigne(separation.parent, separation.id);
    const formeParent = await formeDe(separation.parent);
    if (ligne && !ligne.supprime && ligne.detailLe && formeParent?.detail) {
      const entite = copie(ligne.data);
      return {
        data: formeParent.detail === 'data' ? { success: true, data: entite } : entite,
        headers: {},
        ts: Math.max(ligne.detailLe || 0, synchro),
        origine: 'detail',
      };
    }
  }
  return null;
}
